import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Box,
  Typography,
  Tooltip,
  Popover,
  useMediaQuery,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  ShoppingCart as ShoppingCartIcon,
  MonetizationOn as MonetizationOnIcon,
  LocalShipping as LocalShippingIcon,
  AccountBox as AccountBoxIcon,
  AccountBalance as AccountBalanceIcon,
  PeopleAlt as PeopleAltIcon,
  Summarize as SummarizeIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  PhotoCamera as PhotoCameraIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { canSee } from "../config/permissions";

export const drawerWidth = 240;
export const collapsedWidth = 68;

// Menu structure
const menuStructure = [
  { label: "Dashboard", icon: <DashboardIcon /> },
  { label: "EMPLOYEE PANEL", icon: <PhotoCameraIcon /> },
  {
    label: "PURCHASE",
    icon: <ShoppingCartIcon />,
    children: ["PENDING INTAKES", "PURCHASE INVOICE", "PURCHASE INVOICE PENDING", "BROKER PAYMENTS", "PURCHASE ORDERS", "COMMISSIONS"],
  },
  { label: "SALE", icon: <MonetizationOnIcon />, children: ["SALE INVOICE", "SALE INVOICE PENDING", "SALE ORDERS"] },
  { label: "CONTRACTS", icon: <PeopleAltIcon />, children: ["BOOKING #", "CONTAINER FORM", "PICKUP SCHEDULED"] },
  { label: "INVENTORY", icon: <MonetizationOnIcon />, children: ["PRODUCTS","DAILY CASH","REGRADE MATERIALS", "FINISHED GOODS", "WIRE CHOPPING", "RECOVERY ANALYTICS", "PACKING LIST", "WIRE LIBRARY", "IDENTIFY WIRE"] },
  { label: "FREIGHT", icon: <LocalShippingIcon />, children: ["FREIGHT LANE"] },
  { label: "ACCOUNTS", icon: <AccountBoxIcon /> },
  { label: "EMPLOYEE LOANS", icon: <AccountBoxIcon /> },
  { label: "TRUCKING INVOICES", icon: <LocalShippingIcon /> },
  { label: "COMPANY DOCUMENTS", icon: <DescriptionIcon /> },
  { label: "ACCOUNTING", icon: <AccountBalanceIcon />, children: ["CHART OF ACCOUNTS", "JOURNAL ENTRY", "PROFIT & LOSS", "BALANCE SHEET", "TRIAL BALANCE", "INVENTORY VALUATION", "INVENTORY AGING", "CONTAINER PROFIT", "AR / AP AGING", "GL ACCOUNT DETAIL", "CASH FLOW", "COMPARATIVE P&L", "CUSTOMER STATEMENT", "BANK RECONCILIATION"] },
  { label: "REPORTS", icon: <SummarizeIcon />, children: ["STANDARD REPORTS", "FREIGHT REPORTS", "GENERAL LEDGER", "PRICE DASHBOARD", "PRICE ALERTS"] },
  { label: "SETTINGS", icon: <SettingsIcon />, children: ["QUICKBOOKS", "PRICE MAPPINGS", "MARKET PRICE LINK"] },
  { label: "USERS", icon: <SecurityIcon /> },
];

function filterByAccess(menus, gate, isAdmin) {
  if (isAdmin) return menus;
  return menus
    .map((m) => {
      if (m.children) {
        const allowedChildren = m.children.filter((c) => canSee(gate, c));
        if (!allowedChildren.length) return null;
        return { ...m, children: allowedChildren };
      }
      return canSee(gate, m.label) ? m : null;
    })
    .filter(Boolean);
}

export default function Sidebar({
  mobileOpen,
  setMobileOpen,
  onMenuClick,
  activeContent,
  role,
  modules,
  isPlatformOwner,     // SaaS owner -> sees the Companies management item
  companyName,         // logged-in company (for the brand header)
  collapsed,           // desktop mini-drawer mode
  badges = {},         // { menuLabel: count } -> small amber count chip
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [openMenus, setOpenMenus] = useState({});

  // Diagnostic: detect whether the Sidebar is unexpectedly remounting (which
  // would wipe openMenus) on every interaction.
  useEffect(() => {
    console.log("[SIDEBAR] mounted");
    return () => console.log("[SIDEBAR] UNMOUNTED");
  }, []);

  // Guard against a parent's onClick firing twice in quick succession (which
  // would toggle a submenu open then immediately closed). Ignores a repeat
  // toggle of the same item within 350ms.
  const lastToggleRef = useRef({});

  // Popover used when sidebar is collapsed: clicking a parent icon shows its
  // children in a small flyout panel.
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverMenu, setPopoverMenu]     = useState(null);

  const hasModuleList = Array.isArray(modules);
  const isAdmin = role === "admin";
  const gate = hasModuleList ? modules : (role || "viewer");
  // Stable string key for the module list so it can be a useMemo dependency
  // without tripping the "complex expression in dependency array" lint rule.
  const modulesKey = JSON.stringify(modules);
  // The platform owner manages the SaaS (companies + accounts), NOT any single
  // company's operations — so they only see Companies + Users, never GLOMETS'
  // operational modules. Everyone else gets the normal module-filtered menu.
  //
  // When we have an effective module list (multi-tenant), it is already capped
  // by the company's licensed modules — even for admins — so we filter by it for
  // everyone. The admin "see everything" bypass only applies when there is NO
  // module list (legacy/single-tenant), so a company admin still can't see a
  // module their company didn't license.
  // Memoized so the menu array keeps a stable identity across re-renders;
  // rebuilding it every render made the open submenu collapse on unrelated
  // state updates (e.g. the /auth/me hydrate or polling re-renders).
  const visibleMenus = useMemo(
    () =>
      isPlatformOwner
        ? [
            { label: "COMPANIES", icon: <BusinessIcon /> },
            { label: "USERS", icon: <SecurityIcon /> },
            { label: "AUDIT LOGS", icon: <SummarizeIcon /> },
          ]
        : filterByAccess(menuStructure, gate, isAdmin && !hasModuleList),
    // gate is derived from modules/role; depend on the primitives that define it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPlatformOwner, isAdmin, hasModuleList, role, modulesKey]
  );

  const isMini = collapsed && isDesktop;
  const width = isMini ? collapsedWidth : drawerWidth;

  // Auto-expand parent of active child
  useEffect(() => {
    visibleMenus.forEach((menu) => {
      if (menu.children?.includes(activeContent)) {
        setOpenMenus((prev) => ({ ...prev, [menu.label]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContent]);

  const handleItemClick = (item, hasChildren = false) => {
    if (hasChildren) {
      const now = Date.now();
      if (now - (lastToggleRef.current[item] || 0) < 350) {
        console.log("[SIDEBAR] ignored double-toggle for", item);
        return;
      }
      lastToggleRef.current[item] = now;
      console.log("[SIDEBAR] toggle", item);
      setOpenMenus((prev) => ({ ...prev, [item]: !prev[item] }));
    } else {
      onMenuClick(item);
      if (!isDesktop) setMobileOpen(false);
    }
  };

  const openMiniPopover = (event, menu) => {
    setPopoverAnchor(event.currentTarget);
    setPopoverMenu(menu);
  };
  const closeMiniPopover = () => { setPopoverAnchor(null); setPopoverMenu(null); };

  /* ----- Drawer body ----- */
  const drawerContent = (
    <>
      {/* Brand header */}
      <Box
        sx={{
          minHeight: { xs: 56, sm: 64 },
          display: "flex",
          alignItems: "center",
          justifyContent: isMini ? "center" : "flex-start",
          px: isMini ? 0 : 2,
          gap: 1.25,
          color: "primary.main",
          flexShrink: 0,
        }}
      >
        <BusinessIcon sx={{ fontSize: 30 }} />
        {!isMini && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1, letterSpacing: 0.5 }} noWrap>
              {isPlatformOwner ? "PLATFORM" : (companyName || "GLOMETS")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
              {isPlatformOwner ? "SaaS Admin" : "Container Mgmt"}
            </Typography>
          </Box>
        )}
      </Box>
      <Divider />

      <List sx={{ p: 0, overflowY: "auto" }}>
        {visibleMenus.map((menu) => {
          const isParentActive =
            activeContent === menu.label ||
            (menu.children && menu.children.includes(activeContent));

          // ===== Mini (collapsed) mode =====
          if (isMini) {
            return (
              <Tooltip key={menu.label} title={menu.label} placement="right" arrow>
                <ListItemButton
                  onClick={(e) => {
                    if (menu.children) openMiniPopover(e, menu);
                    else handleItemClick(menu.label, false);
                  }}
                  selected={isParentActive}
                  sx={{
                    justifyContent: "center",
                    py: 1.5,
                    px: 0,
                    borderLeft: "3px solid",
                    borderLeftColor: isParentActive ? "primary.main" : "transparent",
                    "&.Mui-selected": {
                      bgcolor: "action.selected",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: "center",
                      color: isParentActive ? "primary.main" : "text.secondary",
                    }}
                  >
                    {menu.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            );
          }

          // ===== Expanded mode =====
          return (
            <React.Fragment key={menu.label}>
              <ListItemButton
                onClick={() => handleItemClick(menu.label, !!menu.children)}
                selected={isParentActive && !menu.children}
                sx={{
                  py: 1,
                  borderLeft: "3px solid",
                  borderLeftColor: isParentActive ? "primary.main" : "transparent",
                  bgcolor: isParentActive && !menu.children ? "action.selected" : "transparent",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: isParentActive ? "primary.main" : "text.secondary",
                  }}
                >
                  {menu.icon}
                </ListItemIcon>
                <ListItemText
                  primary={menu.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isParentActive ? 700 : 600,
                    letterSpacing: 0.3,
                  }}
                />
                {menu.children
                  ? (openMenus[menu.label] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)
                  : null}
              </ListItemButton>

              {menu.children && (
                <Collapse in={openMenus[menu.label]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                    {menu.children.map((child) => {
                      const childActive = activeContent === child;
                      return (
                        <ListItemButton
                          key={child}
                          sx={{
                            pl: 6,
                            py: 0.6,
                            borderLeft: "3px solid",
                            borderLeftColor: childActive ? "primary.main" : "transparent",
                            bgcolor: childActive ? "action.selected" : "transparent",
                          }}
                          onClick={() => handleItemClick(child)}
                          selected={childActive}
                        >
                          <ListItemText
                            primary={child}
                            primaryTypographyProps={{
                              fontSize: 13,
                              fontWeight: childActive ? 700 : 400,
                              color: childActive ? "primary.main" : "text.primary",
                            }}
                          />
                          {badges[child] > 0 && (
                            <Box
                              sx={{
                                ml: 1, minWidth: 20, height: 18, px: 0.5,
                                borderRadius: 9, bgcolor: "warning.main", color: "#fff",
                                fontSize: 11, fontWeight: 700, lineHeight: "18px",
                                textAlign: "center",
                              }}
                              title={`${badges[child]} invoice(s) need pricing`}
                            >
                              {badges[child]}
                            </Box>
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Mini-mode submenu popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={closeMiniPopover}
        anchorOrigin={{ vertical: "top",  horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 220, ml: 0.5, boxShadow: 4 } }}
      >
        <Box sx={{ px: 1.5, py: 1, bgcolor: "primary.main", color: "primary.contrastText" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.3 }}>
            {popoverMenu?.label}
          </Typography>
        </Box>
        <List dense sx={{ p: 0 }}>
          {popoverMenu?.children?.map((child) => {
            const childActive = activeContent === child;
            return (
              <ListItemButton
                key={child}
                selected={childActive}
                onClick={() => {
                  handleItemClick(child);
                  closeMiniPopover();
                }}
                sx={{
                  py: 0.75,
                  borderLeft: "3px solid",
                  borderLeftColor: childActive ? "primary.main" : "transparent",
                }}
              >
                <ListItemText
                  primary={child}
                  primaryTypographyProps={{
                    fontSize: 13,
                    fontWeight: childActive ? 700 : 400,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </>
  );

  return (
    <Box component="nav" sx={{ width: { md: width }, flexShrink: { md: 0 } }} aria-label="sidebar">
      {/* Mobile drawer (always full-width) */}
      {!isDesktop && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop drawer — animates between drawerWidth and collapsedWidth */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          open
          sx={{
            "& .MuiDrawer-paper": {
              width,
              boxSizing: "border-box",
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
}
